import React from 'react';
import PlanningDayHeader from './PlanningDayHeader';
import PlanningEventCard from './PlanningEventCard';
import PlanningEmptyState from './PlanningEmptyState';
import { Event } from '../../../types/event.types';
import type { EventMetrics } from '../../../types/organizer-events.types';

interface PlanningTimelineProps {
  eventsByDay: Map<string, Event[]>;
  allDayKeys?: string[];
  selectedDateKey: string;
  eventMetrics: Record<string, EventMetrics>;
  metricsLoading: boolean;
  draftsWithoutDate: Event[];
  onEdit: (event: Event) => void;
  onDuplicate: (event: Event) => void;
  onCancel?: (event: Event) => void;
  onRestore?: (event: Event) => void;
  onPublish?: (event: Event) => void;
  onDelete?: (event: Event) => void;
  onInstagram?: (event: Event) => void;
  onAddOccurrence?: (event: Event) => void;
  onCreateEvent: () => void;
  onSwitchToDrafts: () => void;
}

/**
 * PlanningTimeline — Vertical timeline grouped by day
 */
const PlanningTimeline: React.FC<PlanningTimelineProps> = ({
  eventsByDay,
  allDayKeys,
  eventMetrics,
  metricsLoading,
  draftsWithoutDate,
  onEdit,
  onDuplicate,
  onCancel,
  onRestore,
  onPublish,
  onDelete,
  onInstagram,
  onAddOccurrence,
  onCreateEvent,
  onSwitchToDrafts,
}) => {
  // Use allDayKeys if provided (shows empty days too), fallback to eventsByDay keys
  const dayKeys = allDayKeys || Array.from(eventsByDay.keys()).sort();

  return (
    <div className="planning-timeline">
      {/* Render each day — including empty ones */}
      {dayKeys.map(dayKey => {
        const dayEvents = eventsByDay.get(dayKey);
        const hasEvents = dayEvents && dayEvents.length > 0;
        return (
          <div key={dayKey} className="planning-timeline__day" data-day={dayKey}>
            <PlanningDayHeader dateKey={dayKey} eventCount={hasEvents ? dayEvents.length : 0} />
            {hasEvents ? (
              <div className="planning-timeline__cards">
                {dayEvents.map(event => (
                  <PlanningEventCard
                    key={event.id}
                    event={event}
                    metrics={eventMetrics[event.id] || null}
                    metricsLoading={metricsLoading}
                    onEdit={onEdit}
                    onDuplicate={onDuplicate}
                    onCancel={onCancel}
                    onRestore={onRestore}
                    onPublish={onPublish}
                    onDelete={onDelete}
                    onInstagram={onInstagram}
                    onAddOccurrence={onAddOccurrence}
                  />
                ))}
              </div>
            ) : (
              <PlanningEmptyState type="day" onCreateEvent={onCreateEvent} />
            )}
          </div>
        );
      })}

      {/* Drafts without date link */}
      {draftsWithoutDate.length > 0 && (
        <div className="planning-timeline__drafts-link">
          <p className="planning-timeline__drafts-count">
            {draftsWithoutDate.length} {draftsWithoutDate.length === 1 ? 'draft' : 'drafts'} without date
          </p>
          <button
            className="planning-timeline__drafts-button"
            onClick={onSwitchToDrafts}
            type="button"
          >
            View in list view →
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(PlanningTimeline);
